import { logCopilotChatSidePaneTelemetryEvent } from 'owa-copilot-telemetry/lib/copilotChatSidePaneTelemetryLogger';
import { closeCopilotChatDrawerMutator } from '../mutators/closeCopilotChatDrawerMutator';
import { onCopilotChatDrawerLayoutChange } from '../actions/onCopilotChatDrawerLayoutChange';
import { updateCopilotChatDrawerPinnedPaneAction } from '../actions/updateCopilotChatDrawerPinnedPaneAction';
import { isCopilotChatDrawerShown } from '../selectors/isCopilotChatDrawerShown';
import isPopout from 'owa-popout-v2/lib/utils/isPopout';
import { getBizChatWindowKey } from '../utils/getBizChatWindowKey';
import { onCopilotChatDrawerClosed } from '../actions/onCopilotChatDrawerClosed';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import type { MailboxInfo } from 'owa-client-types';
import { clearDisableAutoFocusForCurrentOpen } from '../utils/disableAutoFocusForCurrentOpen';
import { getCopilotChatDrawerOpenedContent } from './openCopilotChatDrawer';
import {
    getSidePaneOpenToCloseSessionId,
    getTimeSinceSidepaneOpened,
} from '../utils/sidePaneOpenToCloseSessionId';

/**
 *
 * @param targetWindow window of the chat to close
 * @param isProgrammatic default false. Whether or not the close was triggered by the user, or programmatic. Used only for telemetry.
 * @param mailboxInfo MailboxInfo for the account. If not provided, falls back to getModuleContextMailboxInfo().
 * @returns
 */
export function closeCopilotChatDrawer(
    targetWindow: Window,
    mailboxInfo?: MailboxInfo,
    isProgrammatic: boolean = false
): void {
    if (!isCopilotChatDrawerShown(targetWindow)) {
        // If Copilot Chat Drawer is not shown, no need to close it.
        // This happens if a caller tries too optimistically to close the drawer
        // without checking if it's already closed.
        return;
    }

    const windowKey = getBizChatWindowKey(targetWindow);
    const timeSinceOpened = getTimeSinceSidepaneOpened(targetWindow);
    const openedContent = getCopilotChatDrawerOpenedContent(targetWindow);

    if (openedContent === 'chat') {
        logCopilotChatSidePaneTelemetryEvent(
            'CopilotSidePane_BizChatClosed',
            {
                isPopout: isPopout(targetWindow),
                windowKey,
                sidePaneOpenToCloseSessionId: getSidePaneOpenToCloseSessionId(targetWindow),
                timeSinceOpened,
                isProgrammatic,
            },
            mailboxInfo
        );
    }

    clearDisableAutoFocusForCurrentOpen();

    closeCopilotChatDrawerMutator(targetWindow);

    // Action for listeners when drawer is closed. mailboxInfo + timeSinceOpened
    // are forwarded so the calendar IDA orchestrator can emit the CopilotPane.Close
    // unified-telemetry datapoint (correlated to its Generate via EditorSessionId).
    onCopilotChatDrawerClosed(
        isProgrammatic,
        windowKey,
        mailboxInfo ?? getModuleContextMailboxInfo(),
        timeSinceOpened,
        openedContent
    );
    onCopilotChatDrawerLayoutChange();

    // Remove the Copilot Chat Drawer as a pinned pane setting, for sticky open.
    // We do this only if we're in main window
    if (targetWindow === window) {
        onCopilotChatDrawerLayoutChange();
        updateCopilotChatDrawerPinnedPaneAction(false /* isSavingAsCopilotChatDrawer */);
    }
}
